import { useEffect } from "react"
import type {
  AppBereich,
  BereichFreigabe,
  EinheitFreigabe,
  TrainingsEinheit,
} from "@nl/shared"

import { useBereichFreigaben, useEinheitFreigaben } from "@/lib/queries"

const TERMIN = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
})

/** "12.10., 09:00 Uhr" */
export function formatFreigabeAb(iso: string): string {
  return `${TERMIN.format(new Date(iso))} Uhr`
}

const TAG = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  weekday: "long",
  day: "numeric",
  month: "long",
})
const UHRZEIT = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  hour: "2-digit",
  minute: "2-digit",
})

/** Day and time apart, for places with room: "Montag, 12. Oktober", "09:00 Uhr". */
export function formatFreigabeTermin(iso: string): { tag: string; uhrzeit: string } {
  const datum = new Date(iso)
  return { tag: TAG.format(datum), uhrzeit: `${UHRZEIT.format(datum)} Uhr` }
}

// setTimeout overflows above ~24.8 days and would fire immediately.
const MAX_TIMEOUT = 2 ** 31 - 1

/**
 * Refetches the moment the next scheduled release is due, so a lock lifts
 * without a reload. The server decides, the timer only asks again.
 */
export function useRefetchZumTermin(
  liste: readonly { freigabeAb?: string }[] | undefined,
  refetch: () => unknown
) {
  const naechste = liste
    ?.map((e) => e.freigabeAb)
    .filter((ab): ab is string => Boolean(ab))
    .sort()[0]

  useEffect(() => {
    if (!naechste) return
    const wartezeit = new Date(naechste).getTime() - Date.now()
    // A second of slack so the server's clock has passed the release too.
    const timer = setTimeout(
      () => void refetch(),
      Math.min(Math.max(wartezeit + 1000, 0), MAX_TIMEOUT)
    )
    return () => clearTimeout(timer)
  }, [naechste, refetch])
}

/** The release state of every unit for the caller's Sparkasse. */
export function useEinheitFreigabeStatus() {
  const query = useEinheitFreigaben()
  const liste = query.data as EinheitFreigabe[] | undefined
  useRefetchZumTermin(liste, query.refetch)

  const nachEinheit = new Map<TrainingsEinheit, EinheitFreigabe>(
    (liste ?? []).map((e) => [e.einheit, e])
  )
  return {
    isLoading: query.isLoading,
    status: (einheit: TrainingsEinheit) => nachEinheit.get(einheit),
    // Nothing counts as open while loading: better a tile that unlocks a
    // moment later than one that locks under the advisor's cursor.
    istOffen: (einheit: TrainingsEinheit) =>
      nachEinheit.get(einheit)?.status === "offen",
  }
}

/** The release state of the whole areas for the caller's Sparkasse. */
export function useBereichFreigabeStatus() {
  const query = useBereichFreigaben()
  const liste = query.data as BereichFreigabe[] | undefined
  useRefetchZumTermin(liste, query.refetch)

  const nachBereich = new Map<AppBereich, BereichFreigabe>(
    (liste ?? []).map((b) => [b.bereich, b])
  )
  return {
    isLoading: query.isLoading,
    status: (bereich: AppBereich) => nachBereich.get(bereich),
    // Unlike units, an unknown state counts as open: the navigation must not
    // flash locks on every page load. The gates wait for `isLoading` instead.
    istGesperrt: (bereich: AppBereich) => {
      const status = nachBereich.get(bereich)?.status
      return status === "gesperrt" || status === "geplant"
    },
  }
}
